/**
 * SSE wire codec — Zod-validated discriminated union for every DebateEvent variant.
 * Invariants: never throws on malformed input (returns null), every accepted event
 * exactly matches the §6 taxonomy in the architecture doc.
 */
import { z } from "zod";
import type { DebateEvent } from "../domain/types";

/* ---------- atom schemas ---------- */

// BULUNAN HATA (2026-07-25 çıktı-yüzeyi denetimi): bu enum yalnızca 7
// legacy rolü sayıyordu, `domain/types.ts::AgentRole` ve backend
// `events.py::AgentRole` ise 13 rol tanımlıyor. `z.discriminatedUnion`
// bilinmeyen bir enum değerinde TÜM frame'i reddeder ve `decodeEvent`
// `null` döner — yani Persona Kütüphanesi'nden konuşlandırılmış bir
// persona (VARSAYILAN rolü `pragmatic-architect`, bkz.
// `persona_library.py:291,496,826`) `persona.hydrate` frame'ini tamamen
// öldürüyordu: konsey hiç render edilmiyordu, tek iz bir console.warn.
// `investigation.plan` olayının bu union'dan eksik olmasıyla AYNI sınıf
// hata (aşağıdaki notu gör) — iki ayrı doğruluk kaynağı, tsc yakalayamaz.
//
// `.catch()` bu dosyanın mevcut konvansiyonu (bkz. `severity`, `status`):
// ileride eklenecek bir rol frame'i öldürmesin. Fallback backend'in
// `_normalise_role` varsayılanıyla aynı.
const RoleSchema = z
  .enum([
    "moderator",
    // Legacy 2024-2025 taksonomisi.
    "strategic-visionary",
    "technical-authority",
    "threat-analyst",
    "contrarian",
    "operational-commander",
    "compliance-officer",
    // 2026 wizard/kütüphane taksonomisi.
    "pragmatic-architect",
    "risk-officer",
    "innovator",
    "devils-advocate",
    "ux-advocate",
    "ethics-steward",
    "financial-steward",
    "people-advocate",
    "customer-voice",
    "evidence-auditor",
    "tempo-strategist",
    "simplifier",
    "scale-planner",
    "stakeholder-navigator",
    "institutional-memory",
    "systems-thinker",
  ])
  .catch("strategic-visionary");

const TurnPhaseSchema = z.enum([
  "idle",
  "retrieving",
  "critiquing",
  "speaking",
  "yielding",
]);

const SeveritySchema = z.enum(["minor", "major", "blocker"]);

const TurnOutcomeSchema = z.enum(["spoke", "forfeited"]);

const InformationAsymmetrySchema = z.object({
  sources: z.array(z.string()),
  blindSpots: z.array(z.string()),
  confidenceCeiling: z.number().min(0).max(1),
});

const PersonaSchema = z
  .object({
    id: z.string().uuid(),
    role: RoleSchema,
    displayName: z.string().min(1),
    // TR corporate honorific resolved deterministically server-side
    // (see orchestrator `honorifics.py`). Optional + default to "Bey"
    // so legacy verdicts/sessions hydrated from the persisted DB before
    // the rollout still pass schema validation. The FE displays this
    // verbatim (no client-side gender guessing).
    honorific: z
      .enum(["Bey", "Hanım"])
      .nullable()
      .optional()
      .transform((v) => v ?? "Bey"),
    // The orchestrator emits `null` (not `undefined`) for missing
    // optional persona fields. Previous schemas only allowed
    // `undefined` via `.optional()` which caused the ENTIRE persona.hydrate
    // event to be rejected — collapsing seat hydration and freezing the
    // debate after the second speaker. Use `.nullable().optional()` so
    // the wire shape `{expertise: null, modelId: null}` validates
    // cleanly. We then transform `null → undefined` so downstream
    // type-checks can stay strict.
    expertise: z
      .string()
      .min(1)
      .max(80)
      .nullable()
      .optional()
      .transform((v) => v ?? undefined),
    systemInstruction: z.string(),
    asymmetry: InformationAsymmetrySchema,
    voiceId: z.string(),
    gender: z
      .enum(["M", "F", "male", "female"])
      .nullable()
      .optional()
      .transform((v) => v ?? undefined),
    voiceRate: z
      .string()
      .max(8)
      .nullable()
      .optional()
      .transform((v) => v ?? "+0%"),
    voicePitch: z
      .string()
      .max(10)
      .nullable()
      .optional()
      .transform((v) => v ?? "+0Hz"),
    voiceVolume: z
      .string()
      .max(8)
      .nullable()
      .optional()
      .transform((v) => v ?? "+0%"),
    modelId: z
      .string()
      .min(1)
      .max(80)
      .nullable()
      .optional()
      .transform((v) => v ?? undefined),
    themeColor: z.string(),
    seatIndex: z.number().int().min(0),
    // Backend-only metadata that the FE doesn't care about but used to
    // crash schema validation by sneaking through as an unknown field
    // when later events tightened. Declare it explicitly as a passthrough
    // so future additions on the orchestrator side don't break the wire.
    samplingOverrides: z.unknown().nullable().optional(),
  })
  // Forward-compatibility insurance — accept extra wire fields silently
  // instead of nuking the whole persona.hydrate frame. Critical because
  // the orchestrator evolves faster than the FE schema; a single new
  // backend field used to freeze the entire debate stream.
  .passthrough();

const CritiqueSchema = z.object({
  targetTurnId: z.string(),
  flaw: z.string().min(1),
  severity: SeveritySchema,
});

const ConsensusSnapshotSchema = z.object({
  round: z.number().int().min(0),
  agreementMatrix: z.array(z.array(z.number())),
  scalarScore: z.number().min(0).max(1),
  deadlock: z.boolean(),
  shouldFinalize: z.boolean(),
});

const SimulationOutcomeSchema = z.object({
  scenario: z.string().min(1).max(200),
  survived: z.boolean(),
  rationale: z.string().max(800).default(""),
  failure_category: z.string().nullable().optional(),
  missing_control: z.string().nullable().optional(),
  severity: z.enum(["low", "medium", "high", "critical"]).nullable().optional(),
  recommended_fix: z.string().nullable().optional(),
}).passthrough();

const VerdictSchema = z.object({
  executiveSummary: z.string().min(1),
  dissentingLog: z.array(
    z.object({
      agentId: z.string(),
      concern: z.string(),
      severity: SeveritySchema,
    }),
  ),
  actionPlan: z
    .object({
      architectural: z.array(z.unknown()).default([]),
      strategic: z.array(z.unknown()).default([]),
      tactical: z.array(z.unknown()).default([]),
      immediate: z.array(z.unknown()).optional().default([]),
      governance: z.array(z.unknown()).optional().default([]),
      risks: z.array(z.unknown()).optional().default([]),
      kpis: z.array(z.unknown()).optional().default([]),
      criticalAssumptions: z.array(z.string()).optional().default([]),
    })
    .passthrough(),
  generatedAt: z.number(),
  // AGI plan EPIC 2 — chaos-rollout telemetry. Both fields are
  // optional/legacy-friendly so historical verdicts (pre-EPIC 2)
  // still pass the schema after the upgrade.
  simulationScore: z.number().min(0).max(1).nullable().optional(),
  simulationOutcomes: z.array(SimulationOutcomeSchema).optional().default([]),
  baselineSimulationScore: z.number().min(0).max(1).nullable().optional(),
  hardenedSimulationScore: z.number().min(0).max(1).nullable().optional(),
  simulationRuns: z.array(z.record(z.unknown())).optional().default([]),
  resilienceStatus: z.string().nullable().optional(),
  hardenedActionPlan: z.record(z.unknown()).nullable().optional(),
  confidenceMetrics: z.record(z.unknown()).nullable().optional(),
  boardBrief: z.record(z.unknown()).nullable().optional(),
  decisionMemory: z.record(z.unknown()).nullable().optional(),
  commentaryAnalysis: z.string().nullable().optional(),
  commentaryCritique: z.string().nullable().optional(),
  // İş Kolu E4 — code-computed (never LLM-generated) traceable
  // citation bibliography. Absent/empty for legacy verdicts.
  evidenceBibliography: z
    .array(
      z.object({
        name: z.string(),
        url: z.string().default(""),
        citedByTurnCount: z.number().int().min(0),
        citedBySpeakerIds: z.array(z.string()).default([]),
      }),
    )
    .optional()
    .default([]),
  // Görev 24 — single FE-visible degradation flag. Absent/null on
  // legacy verdicts — treat the same as "ok".
  evidenceQuality: z.enum(["ok", "degraded"]).nullable().optional(),
  // Görev 18 — unified evidence-registry conflicts; loosely typed
  // records, empty array means "audit ran, nothing found".
  evidenceConflicts: z.array(z.record(z.unknown())).optional().default([]),
  // Görev 20/21/22 — BLUF, decision manifest, verdict state machine,
  // decision-type classification. All nullable/optional + loosely
  // typed: the backend contract for these fields is still evolving,
  // so we deliberately do NOT lock down sub-shapes here — the FE
  // components that consume them read defensively via optional
  // chaining and hide gracefully when a sub-field is missing.
  bluf: z.record(z.unknown()).nullable().optional(),
  decisionManifest: z.record(z.unknown()).nullable().optional(),
  verdictStatus: z.record(z.unknown()).nullable().optional(),
  decisionType: z.record(z.unknown()).nullable().optional(),
}).passthrough();

/* ---------- discriminated union — DebateEvent ---------- */

export const DebateEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("persona.hydrate"),
    personas: z.array(PersonaSchema).min(1),
    seatOrder: z.array(z.string().uuid()).min(1),
  }),
  z.object({
    type: z.literal("turn.start"),
    turnId: z.string(),
    speakerId: z.string(),
    roundIndex: z.number().int().min(0),
  }),
  z.object({
    type: z.literal("turn.phase"),
    turnId: z.string(),
    phase: TurnPhaseSchema,
  }),
  z.object({
    type: z.literal("turn.critique"),
    turnId: z.string(),
    critique: CritiqueSchema,
  }),
  z.object({
    type: z.literal("turn.token"),
    turnId: z.string(),
    token: z.string(),
    seq: z.number().int().min(0),
  }),
  z.object({
    type: z.literal("turn.end"),
    turnId: z.string(),
    finalText: z.string(),
    outcome: TurnOutcomeSchema,
    forfeitReason: z.string().nullable().optional(),
    researchSources: z
      .array(
        z.object({
          name: z.string(),
          title: z.string(),
          // z.string().url() uses the WHATWG URL parser which rejects many
          // valid research URLs (IDN domains, query-param edge-cases, bare
          // protocol-relative refs returned by certain search APIs).
          // Using .min(1) here: the frontend only renders the URL as an
          // anchor href — it does NOT need Zod to validate it.
          url: z.string().default(""),
          summary: z.string().default(""),
          published: z.string().nullable().optional(),
        }),
      )
      .optional()
      .default([]),
    ragCitations: z
      .array(
        z.object({
          docId: z.string(),
          filename: z.string(),
          chunkIdx: z.number().int().min(0),
          score: z.number().min(0).max(1),
          scope: z.enum(["pool", "private"]).default("pool"),
          excerpt: z.string().default(""),
        }),
      )
      .optional()
      .default([]),
    // İş Kolu E2 — previously computed server-side (`audit_citation_adherence`)
    // and only ever reached a structured log line. null when the turn had
    // zero research sources (nothing to audit).
    citationAudit: z
      .object({
        sourceCount: z.number().int().min(0),
        citedCount: z.number().int().min(0),
        adherenceScore: z.number().min(0).max(1),
        citedSources: z.array(z.string()).default([]),
        missingSources: z.array(z.string()).default([]),
        isLowAdherence: z.boolean().default(false),
        // İş Kolu G3 — Grounded Verifier (numeric-claim edition).
        numericClaims: z.array(z.string()).default([]),
        ungroundedNumericClaims: z.array(z.string()).default([]),
        // İş Kolu BENCHMARK — fabricated-authority detection.
        fabricatedAuthorities: z.array(z.string()).default([]),
      })
      .nullable()
      .optional(),
  }),
  z.object({
    type: z.literal("turn.research"),
    turnId: z.string(),
    query: z.string(),
    sources: z.array(
      z.object({
        name: z.string(),
        title: z.string(),
        url: z.string().default(""), // lenient — see turn.end comment above
        summary: z.string().default(""),
        published: z.string().nullable().optional(),
      }),
    ),
    elapsedMs: z.number().int().min(0),
    cacheHit: z.boolean().optional(),
  }),
  z.object({
    type: z.literal("turn.rag"),
    turnId: z.string(),
    query: z.string(),
    citations: z.array(
      z.object({
        docId: z.string(),
        filename: z.string(),
        chunkIdx: z.number().int().min(0),
        score: z.number().min(0).max(1),
        scope: z.enum(["pool", "private"]).default("pool"),
        excerpt: z.string().default(""),
      }),
    ),
  }),
  // Internal-thinking trace — emitted when the LLM exposes its
  // reasoning channel via vLLM's `delta.reasoning`. The FE renders
  // this as a collapsed "[uzman düşündü]" disclosure. Was missing
  // from the schema → every reasoning frame was rejected → the
  // store never got the complementary `turn.token`s → playback
  // froze after the second speaker. See domain/types.ts L249-257.
  z.object({
    type: z.literal("turn.thinking"),
    turnId: z.string(),
    chars: z.number().int().min(0),
    summary: z.string(),
  }),
  z.object({
    type: z.literal("consensus.update"),
    snapshot: ConsensusSnapshotSchema,
  }),
  z.object({
    type: z.literal("moderator.interrupt"),
    pivot: z.string(),
  }),
  z.object({
    type: z.literal("moderator.resolve"),
  }),
  z.object({
    type: z.literal("debate.finalize"),
    // Konsey Cognitive Renaissance — Bölüm A1.
    // `max_rounds` reason yeni eklendi: kullanıcı seçtiği tur bütçesi
    // tükendi ama gerçek consensus ya da deadlock olmadı. FE pill rengi
    // info-blue (warn-amber değil).
    reason: z.enum(["consensus", "deadlock", "user", "max_rounds"]),
  }),
  z.object({
    type: z.literal("verdict.ready"),
    verdict: VerdictSchema,
  }),
  z.object({
    type: z.literal("resilience.progress"),
    phase: z.enum([
      "simulation.baseline.started",
      "simulation.baseline.completed",
      "verdict.hardening.started",
      "verdict.hardening.completed",
      "simulation.hardened.started",
      "simulation.hardened.completed",
    ]),
    sessionId: z.string().optional(),
    progress: z.number().min(0).max(1).optional(),
    message: z.string().optional(),
  }),
  z.object({
    type: z.literal("stream.error"),
    code: z.string(),
    message: z.string(),
  }),
  // Konsey Cognitive Renaissance — Bölüm B3 (eventCodec fix).
  // user.interject SSE event'i daha önce schema'da yoktu → codec
  // her frame'i reddediyordu → store hiç güncellenmiyordu →
  // applied/queued status hiçbir zaman uygulanmıyordu.
  z.object({
    type: z.literal("user.interject"),
    id: z.string(),
    text: z.string(),
    receivedAt: z.number(),
    appliedAtRound: z.number().int().nullable(),
    status: z.enum(["queued", "applied"]),
  }),
  // Konsey Cognitive Renaissance — Bölüm A2.
  // Sycophancy detection — konsey aşırı uyumlu, FE banner gösterir.
  z.object({
    type: z.literal("sycophancy.alert"),
    round: z.number().int().min(0),
    score: z.number().min(0).max(1),
    dispersion: z.number().min(0).max(1),
    consecutive: z.number().int().min(1),
    devil_advocate_invited: z.boolean().optional().default(false),
  }),
  // Konsey Cognitive Renaissance — Bölüm D2.
  // Per-persona BrainState snapshot stream (throttled 400ms backend).
  z.object({
    type: z.literal("brain.update"),
    personaId: z.string(),
    turnIndex: z.number().int().min(0),
    snapshot: z.record(z.unknown()),
  }),
  // ── Kalite Planı — yeni additive event'ler ──────────────────────────
  // Tamamı OPSİYONEL akışlardır: backend göndermezse UI eski davranışını
  // korur. Payload objeleri `.passthrough()` taşır (persona.hydrate
  // dersinden: tek bir yeni backend alanı bütün frame'i öldürmesin) ve
  // skaler alanlar default'ludur — yarım payload event'i düşürmez.
  z.object({
    type: z.literal("turn.plan"),
    turnId: z.string(),
    speakerId: z.string(),
    plan: z
      .object({
        targetRequirements: z.array(z.string()).default([]),
        angle: z.string().default(""),
        evidenceToUse: z.array(z.string()).default([]),
        rebut: z
          .string()
          .nullable()
          .optional()
          .transform((v) => v ?? null),
      })
      .passthrough(),
  }),
  z.object({
    type: z.literal("turn.verify"),
    turnId: z.string(),
    speakerId: z.string(),
    verdict: z
      .object({
        ok: z.boolean(),
        judgeUsed: z.boolean().default(false),
        material: z.boolean().default(false),
        issues: z
          .array(
            z
              .object({
                kind: z.string().default(""),
                span: z.string().default(""),
                evidence: z.string().default(""),
                // `.catch("low")` — bilinmeyen yeni severity değeri
                // tüm verify frame'ini düşürmesin (rozet yine çıksın).
                severity: z.enum(["low", "med", "high"]).catch("low"),
              })
              .passthrough(),
          )
          .default([]),
      })
      .passthrough(),
    healJumps: z.number().int().min(0).default(0),
  }),
  z.object({
    type: z.literal("subagent.result"),
    kind: z.enum(["micro_expert", "research", "verifier", "compute"]),
    result: z
      .object({
        kind: z.string().default(""),
        ok: z.boolean(),
        summary: z.string().default(""),
        evidenceUrls: z.array(z.string()).default([]),
        // Aralık dayatması YOK (min/max reddi frame öldürür) — FE
        // yüzde gösterirken kendisi clamp'ler.
        confidence: z.number().default(0),
        error: z
          .string()
          .nullable()
          .optional()
          .transform((v) => v ?? null),
        computation: z
          .object({
            code: z.string().default(""),
            stdout: z.string().default(""),
            outputHash: z.string().default(""),
          })
          .passthrough()
          .nullable()
          .optional()
          .transform((v) => v ?? null),
      })
      .passthrough(),
  }),
  z.object({
    type: z.literal("moderator.summon"),
    witnessId: z.string(),
    witnessName: z.string(),
    requirement: z.string().default(""),
  }),
  z.object({
    type: z.literal("coverage.update"),
    matrix: z
      .object({
        deliverableType: z.string().default(""),
        score: z.number().default(0),
        requirements: z
          .array(
            z
              .object({
                id: z.string().default(""),
                text: z.string(),
                kind: z.string().default(""),
                status: z
                  .enum(["covered", "partial", "uncovered"])
                  .catch("uncovered"),
                evidenceTurnIds: z.array(z.string()).default([]),
              })
              .passthrough(),
          )
          .default([]),
      })
      .passthrough(),
  }),
  z.object({
    type: z.literal("entity.graph"),
    graph: z
      .object({
        sealed: z.boolean().default(false),
        nodes: z
          .array(
            z
              .object({
                canonical: z.string(),
                type: z.string().default(""),
                salience: z.number().default(0),
                reliability: z.number().default(0),
                evidenceUrls: z.array(z.string()).default([]),
                rejected: z.boolean().default(false),
              })
              .passthrough(),
          )
          .default([]),
        edges: z
          .array(
            z
              .object({
                src: z.string(),
                dst: z.string(),
                relation: z.string().default(""),
                evidenceUrl: z.string().default(""),
                confidence: z.number().default(0),
              })
              .passthrough(),
          )
          .default([]),
      })
      .passthrough(),
  }),
  // Kapsam-Güdümlü Soruşturma — `investigation` topolojisinin canlı hücre
  // defteri. BULUNAN HATA (kapsamlı öz-denetim): bu event TypeScript
  // tipinde (`domain/types.ts::InvestigationPlanEvent`) mevcuttu ama BU
  // runtime Zod şemasında YOKTU — `z.discriminatedUnion` tanımadığı
  // `type` değerini SESSİZCE reddeder, yani backend'in yolladığı HER
  // `investigation.plan` frame'i `decodeEvent()` tarafından `null`'a
  // düşürülüyordu (tsc bunu asla yakalayamaz — iki ayrı doğruluk kaynağı).
  // `entity.graph`/`coverage.update` ile AYNI gevşek/`.passthrough()`
  // sözleşmesi: backend alan eklerse frame ölmez.
  z.object({
    type: z.literal("investigation.plan"),
    plan: z
      .object({
        query: z.string().default(""),
        coverageScore: z.number().default(0),
        cells: z
          .array(
            z
              .object({
                id: z.string().default(""),
                entity: z.string(),
                entityType: z.string().default("other"),
                aspect: z.string().default(""),
                priority: z.number().default(0.5),
                status: z
                  .enum(["open", "partial", "covered", "no_evidence"])
                  .catch("open"),
                mustCover: z.boolean().default(false),
                openQuestions: z.array(z.string()).default([]),
                evidenceTurnIds: z.array(z.string()).default([]),
                citationUrls: z.array(z.string()).default([]),
              })
              .passthrough(),
          )
          .default([]),
      })
      .passthrough(),
  }),
]);

export interface CodecLogger {
  warn(msg: string, meta?: unknown): void;
}

const consoleLogger: CodecLogger = {
  warn: (msg, meta) => {
    if (typeof console !== "undefined") {
      console.warn(`[eventCodec] ${msg}`, meta ?? "");
    }
  },
};

export function safeJsonParse(line: string): unknown | null {
  try {
    return JSON.parse(line);
  } catch {
    return null;
  }
}

export function decodeEvent(
  raw: unknown,
  logger: CodecLogger = consoleLogger,
): DebateEvent | null {
  const parsed = DebateEventSchema.safeParse(raw);
  if (!parsed.success) {
    logger.warn("rejected SSE frame", {
      issues: parsed.error.issues.slice(0, 3),
    });
    return null;
  }
  // Zod's inferred type is a structurally-equivalent superset; cast is safe
  // because the discriminated union mirrors DebateEvent 1:1 (verified via tests).
  return parsed.data as DebateEvent;
}
